/* ==========================================================================
   fk-store-ui.js — the visible half of the store context system.

   Renders the header pill, the store picker dialog, the optional zip check,
   and substitutes {city} into any context-aware copy on the page.

   NOTE ON GEO: there is deliberately no automatic geo lookup here. Shopify
   exposes country, not metro, and a client-side geo call means adding a
   third-party script — which AGENTS.md §10 does not permit without approval.
   Context is therefore set only by explicit action: the pill, the picker, or
   landing on a store / Ready Today page. Automatic suggestion is flagged in
   IMPLEMENT-LATER.md (A8). The design already handles this: an unset context
   simply renders the national copy, which is correct for most visitors.
   ========================================================================== */

(function () {
  'use strict';

  function ctx() { return window.FK && window.FK.StoreContext; }

  /* Where "Shop this store" points. Carried in FKConfig by fk-store-data so the
     route is built by Liquid (and stays correct under a subfolder locale)
     rather than being written into this file. The literal is only a floor for
     the case where the snippet has not run — the page would be badly broken
     already at that point, but a dead link is worse than a plain one. */
  /* Each shop has its own shelf collection, which is a filterable collection
     route; the generic page is not. Prefer the store's own URL and fall back
     to the global one, so a store record written before this existed, or a
     call with no key, still resolves. */
  function readyUrl(key) {
    var stores = (window.FKStores && window.FKStores.stores) || [];
    for (var i = 0; i < stores.length; i++) {
      if (key && stores[i].key === key && stores[i].readyUrl) return stores[i].readyUrl;
    }
    return (window.FKConfig && window.FKConfig.readyUrl) || '/pages/ready-today';
  }

  /* ------------------------------------------------------------------ */
  /* Pill                                                                */
  /* ------------------------------------------------------------------ */

  function pillLabel() {
    var C = ctx();
    if (!C) return 'Choose your store';

    var key = C.get();
    if (key === 'none') return 'Choose your store';

    var store = C.getStore(key);
    var city = store ? store.city : key.charAt(0).toUpperCase() + key.slice(1);

    /* Unconfirmed guess — never claim to know. */
    if (!C.isConfirmed()) return city + '?';

    return city + ' · Ready today';
  }

  function renderPills() {
    var C = ctx();
    document.querySelectorAll('[data-fk-pill-label]').forEach(function (el) {
      el.textContent = pillLabel();
    });
    document.querySelectorAll('[data-fk-store-pill]').forEach(function (el) {
      el.setAttribute('data-fk-state', C ? C.get() : 'none');
      el.setAttribute('data-fk-confirmed', C && C.isConfirmed() ? 'true' : 'false');
    });
  }

  /* ------------------------------------------------------------------ */
  /* {city} substitution — announcement bar, headings, any opted-in copy  */
  /* ------------------------------------------------------------------ */

  function renderCityTokens() {
    var C = ctx();
    var store = C ? C.store() : null;
    var city = store ? store.city : '';

    document.querySelectorAll('[data-fk-city-target]').forEach(function (el) {
      if (!el.hasAttribute('data-fk-city-template')) {
        el.setAttribute('data-fk-city-template', el.textContent);
      }
      var tpl = el.getAttribute('data-fk-city-template') || '';
      el.textContent = tpl.replace(/\{city\}/g, city);
    });

    /* Plain text nodes that just want the city name. */
    document.querySelectorAll('[data-fk-city]').forEach(function (el) {
      el.textContent = city;
    });

    /* Neighbourhood, used by the availability block and store copy. */
    document.querySelectorAll('[data-fk-hood]').forEach(function (el) {
      el.textContent = store && store.hood ? store.hood : (city || '');
    });
  }

  /* ------------------------------------------------------------------ */
  /* Picker                                                              */
  /* ------------------------------------------------------------------ */

  var lastFocused = null;

  function storeCard(store) {
    var C = ctx();
    var status = C ? C.status(store.key) : null;
    var selected = C && C.get() === store.key && C.isConfirmed();

    var statusClass = 'fk-status--closed';
    var statusDot = 'fk-dot--closed';
    var statusText = 'Hours unavailable';

    if (status && status.known) {
      if (status.state === 'open') {
        statusClass = 'fk-status--open';
        statusDot = 'fk-dot--open';
        statusText = 'Open till ' + C.formatHour(status.closeHour);
      } else if (status.state === 'before-open') {
        statusClass = 'fk-status--later';
        statusDot = 'fk-dot--later';
        statusText = 'Opens ' + C.formatHour(status.openHour) + ' today';
      } else {
        statusClass = 'fk-status--closed';
        statusDot = 'fk-dot--closed';
        statusText = status.nextOpenHour != null
          ? 'Closed · opens ' + C.formatHour(status.nextOpenHour) + ' ' + status.nextOpenDayName
          : 'Closed';
      }
    }

    var el = document.createElement('div');
    el.className = 'fk-picker-card' + (selected ? ' is-selected' : '');

    var addr = [store.address1, store.address2].filter(Boolean).join(', ');

    el.innerHTML =
      '<div class="fk-picker-card__body">' +
        '<p class="fk-picker-card__city">' + escapeHTML(store.city) + '</p>' +
        '<p class="fk-picker-card__hood">' + escapeHTML(store.hood || '') + '</p>' +
        '<p class="fk-picker-card__addr">' + escapeHTML(addr) + '</p>' +
        '<span class="fk-status ' + statusClass + '">' +
          '<span class="fk-dot ' + statusDot + '"></span>' + escapeHTML(statusText) +
        '</span>' +
      '</div>' +
      '<div class="fk-picker-card__actions">' +
        (store.mapUrl
          ? '<a class="fk-btn fk-btn--secondary fk-btn--square" href="' + escapeAttr(store.mapUrl) + '" target="_blank" rel="noopener">Get directions</a>'
          : '') +
        '<button type="button" class="fk-btn fk-btn--primary fk-btn--square" data-fk-pick="' + escapeAttr(store.key) + '">' +
          (selected ? 'Your store' : 'Shop this store') +
        '</button>' +
      '</div>';

    return el;
  }

  function renderPicker() {
    var C = ctx();
    var host = document.querySelector('[data-fk-picker-stores]');
    if (!host || !C) return;

    host.innerHTML = '';
    var stores = C.stores();

    if (!stores.length) {
      host.innerHTML = '<p class="fk-picker__empty">Store details are being updated. Everything on the site still ships free over ' +
        formatMoney(window.FKConfig && window.FKConfig.freeShipThreshold) + '.</p>';
      return;
    }

    stores.forEach(function (s) { host.appendChild(storeCard(s)); });
  }

  function openPicker() {
    var el = document.querySelector('[data-fk-picker]');
    if (!el) return;
    lastFocused = document.activeElement;
    renderPicker();
    el.hidden = false;
    document.documentElement.classList.add('fk-no-scroll');
    document.querySelectorAll('[data-fk-store-pill]').forEach(function (p) {
      p.setAttribute('aria-expanded', 'true');
    });
    var focusable = el.querySelector('button, a, input');
    if (focusable) focusable.focus();
  }

  function closePicker() {
    var el = document.querySelector('[data-fk-picker]');
    if (!el || el.hidden) return;
    el.hidden = true;
    /* fk-no-scroll is a shared boolean, not a counter. The drawer now hosts a
       store pill too, so the picker and the drawer can be open at once — and
       backing out of the picker used to unlock the page under a still-open
       drawer. Only release the lock when nothing else is holding it. */
    var navDrawer = document.querySelector('[data-fk-nav-drawer]');
    if (!navDrawer || navDrawer.hidden) {
      document.documentElement.classList.remove('fk-no-scroll');
    }
    document.querySelectorAll('[data-fk-store-pill]').forEach(function (p) {
      p.setAttribute('aria-expanded', 'false');
    });
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  /* ------------------------------------------------------------------ */
  /* Store cards — homepage "Come say hi" and the Ready Today store step  */
  /* ------------------------------------------------------------------ */

  function statusPill(store) {
    var C = ctx();
    var s = C ? C.status(store.key) : null;
    if (!s || !s.known) {
      return { cls: 'fk-status--closed', dot: 'fk-dot--closed', text: 'Hours unavailable' };
    }
    if (s.state === 'open') {
      return { cls: 'fk-status--open', dot: 'fk-dot--open', text: 'Open till ' + C.formatHour(s.closeHour) };
    }
    if (s.state === 'before-open') {
      return { cls: 'fk-status--later', dot: 'fk-dot--later', text: 'Opens ' + C.formatHour(s.openHour) + ' today' };
    }
    return {
      cls: 'fk-status--closed',
      dot: 'fk-dot--closed',
      text: s.nextOpenHour != null
        ? 'Closed · opens ' + C.formatHour(s.nextOpenHour) + ' ' + s.nextOpenDayName
        : 'Closed'
    };
  }

  function todaysHours(store) {
    var C = ctx();
    var s = C ? C.status(store.key) : null;
    if (!s || !s.known || s.openHour == null) return 'Closed today';
    return C.formatHour(s.openHour) + ' – ' + C.formatHour(s.closeHour);
  }


  /* Full week, with consecutive identical days collapsed into ranges —
     "Mon–Wed 10 AM – 4 PM" rather than three rows. The prototype shows the
     whole week; the first build showed today only. */
  function weekRows(store) {
    var C = ctx();
    if (!C || !store.week || store.week.length !== 7) return '';
    var SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var today = C.now().dow;

    var label = function (pair) {
      return (pair && pair.length === 2 && pair[0] != null)
        ? C.formatHour(pair[0]) + ' – ' + C.formatHour(pair[1])
        : 'Closed';
    };

    var groups = [];
    for (var i = 0; i < 7; i++) {
      var txt = label(store.week[i]);
      var last = groups[groups.length - 1];
      if (last && last.txt === txt) last.end = i;
      else groups.push({ start: i, end: i, txt: txt });
    }

    return groups.map(function (g) {
      var days = g.start === g.end ? SHORT[g.start] : SHORT[g.start] + '\u2013' + SHORT[g.end];
      var isToday = today >= g.start && today <= g.end;
      return '<div class="fk-store-card__hourrow' + (isToday ? ' is-today' : '') + '">' +
        '<span>' + days + '</span><span>' + escapeHTML(g.txt) + '</span></div>';
    }).join('');
  }

  function renderStoreCards() {
    var C = ctx();
    var hosts = document.querySelectorAll('[data-fk-store-cards]');
    if (!hosts.length || !C) return;

    var stores = C.stores();

    hosts.forEach(function (host) {
      host.innerHTML = '';

      if (!stores.length) {
        host.innerHTML = '<p class="fk-picker__empty">Store details are being set up.</p>';
        return;
      }

      stores.forEach(function (store) {
        var pill = statusPill(store);
        var addr = [store.address1, store.address2].filter(Boolean).join(', ');
        var card = document.createElement('div');
        card.className = 'fk-store-card';

        /* The shop photo. Comes from Customizer → Flor Keeps — Stores → Shop
           photo. Renders an empty blush panel when unset rather than nothing,
           so the card keeps its shape either way. */
        var media = '<div class="fk-store-card__media">' +
          (store.image
            ? '<img src="' + escapeAttr(store.image) + '" alt="' + escapeAttr(store.city) + '" loading="lazy">'
            : '') +
          '</div>';

        card.innerHTML =
          media +
          '<div class="fk-store-card__body">' +
            '<div class="fk-store-card__row">' +
              '<div class="fk-store-card__ident">' +
                '<p class="fk-store-card__city">' + escapeHTML(store.city) + '</p>' +
                '<p class="fk-store-card__hood">' + escapeHTML(store.hood || '') + '</p>' +
                '<p class="fk-store-card__addr">' +
                  escapeHTML(store.address1 || '') + '<br>' + escapeHTML(store.address2 || '') +
                '</p>' +
              '</div>' +
              '<div class="fk-store-card__hours">' + weekRows(store) + '</div>' +
            '</div>' +
            '<span class="fk-status ' + pill.cls + '">' +
              '<span class="fk-dot ' + pill.dot + '"></span>' + escapeHTML(pill.text) +
            '</span>' +
            '<div class="fk-store-card__actions">' +
              (store.mapUrl
                ? '<a class="fk-btn fk-btn--secondary fk-btn--square" href="' + escapeAttr(store.mapUrl) + '" target="_blank" rel="noopener">Get directions</a>'
                : '') +
              /* A LINK, not a button, and NOT the data-fk-pick hook.

                 data-fk-pick belongs to the picker DIALOG, where "shop this
                 store" means "choose this one and close the dialog" — the
                 handler calls preventDefault and closePicker() so the shopper
                 stays where they were, mid-task. On the Visit Us page and the
                 homepage there is no dialog to close, so pressing this set the
                 store context and then did visibly nothing: the click was
                 swallowed and the page never moved.

                 Here the button means "take me to this shop's shelf", so it is
                 an anchor to Ready Today carrying the store key. Same pattern
                 the store detail page already uses (data-fk-store-confirm on an
                 <a>): set the context on the way out, never preventDefault.
                 Ready Today renders both shelves and reveals the one matching
                 the context, so setting it first is what makes the right
                 shelf show on arrival. */
              '<a class="fk-btn fk-btn--primary fk-btn--square" href="' + escapeAttr(readyUrl(store.key)) + '" data-fk-store-confirm="' + escapeAttr(store.key) + '">Shop this store</a>' +
            '</div>' +
          '</div>';

        host.appendChild(card);
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Store detail page                                                    */
  /* ------------------------------------------------------------------ */

  var DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  function renderStorePage() {
    var C = ctx();
    var page = document.querySelector('[data-fk-store-page]');
    if (!page || !C) return;

    var key = page.getAttribute('data-fk-store-page');
    var store = C.getStore(key);
    if (!store) return;

    var s = C.status(key);
    var set = function (sel, value) {
      var el = page.querySelector(sel);
      if (el) el.textContent = value;
    };

    set('[data-fk-store-page-hood]', store.hood || '');
    set('[data-fk-store-page-address]', [store.address1, store.address2].filter(Boolean).join(', '));
    set('[data-fk-store-page-hours]', s.openHour != null
      ? C.formatHour(s.openHour) + ' – ' + C.formatHour(s.closeHour) + ' today'
      : 'Closed today');
    set('[data-fk-store-page-perk-title]', store.perkTitle || '');
    set('[data-fk-store-page-perk-body]', store.perkBody || '');

    var map = page.querySelector('[data-fk-store-page-map]');
    if (map && store.mapUrl) map.setAttribute('href', store.mapUrl);

    var pill = page.querySelector('[data-fk-store-page-status]');
    if (pill) {
      var p = statusPill(store);
      pill.className = 'fk-status ' + p.cls;
      pill.innerHTML = '<span class="fk-dot ' + p.dot + '"></span>' + escapeHTML(p.text);
    }

    var week = page.querySelector('[data-fk-store-page-week]');
    if (week && store.week && store.week.length === 7) {
      var today = C.now().dow;
      var rows = '';
      for (var i = 0; i < 7; i++) {
        var pair = store.week[i];
        var hours = pair && pair.length === 2 && pair[0] != null
          ? C.formatHour(pair[0]) + ' – ' + C.formatHour(pair[1])
          : 'Closed';
        rows += '<tr' + (i === today ? ' class="is-today"' : '') + '>' +
          '<th scope="row">' + DAY_LABELS[i] + '</th><td>' + hours + '</td></tr>';
      }
      week.innerHTML = '<tbody>' + rows + '</tbody>';
    }
  }

  /* ------------------------------------------------------------------ */
  /* Zip check — messaging only, never a gate                            */
  /* ------------------------------------------------------------------ */

  function runZipCheck(scope) {
    var C = ctx();
    var input = scope.querySelector('[data-fk-zip-input]');
    var out = scope.querySelector('[data-fk-zip-result]');
    if (!input || !out || !C) return;

    var result = C.zoneForZip(input.value);
    var threshold = formatMoney(window.FKConfig && window.FKConfig.freeShipThreshold);

    if (!result) {
      out.textContent = 'Enter a 5-digit zip code.';
      out.className = 'fk-picker__zip-result';
      return;
    }

    var store = result.store ? C.getStore(result.store) : null;
    var city = store ? store.city : '';

    /* The 'pickup' branch was retired with the zipDrive list on 2026-08-28 —
       see zoneForZip in fk-store-context.js. Two zones now: delivery, national.

       The radius is deliberately NOT named any more. It was read from
       FKConfig.deliveryRadiusMiles and printed as "within 30 miles of Austin",
       but the same-day lists are measured zip sets, not a circle, so the
       sentence asserted a geometry the data does not have. */
    if (result.zone === 'delivery') {
      out.textContent = 'Good news — ' + city +
        ' delivers to you. Local delivery & pickup available.';
      out.className = 'fk-picker__zip-result is-win';
    } else {
      /* Never a rejection — always a buyable path. */
      out.textContent = 'We ship to you — free over ' + threshold + ', arriving in 3–5 business days.';
      out.className = 'fk-picker__zip-result is-ok';
    }
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function escapeHTML(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function escapeAttr(s) { return escapeHTML(s); }

  function formatMoney(amount) {
    if (amount == null) return '';
    var n = Number(amount);
    if (isNaN(n)) return '';
    return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  }

  /* ------------------------------------------------------------------ */
  /* Ready Today links - one destination per visitor                     */
  /* ------------------------------------------------------------------ */

  /* Ready Today now lives at TWO addresses - /collections/ready-austin and
     /collections/ready-dallas - plus the neutral /pages/ready-today for a
     shopper who has not picked a shop yet. Every touchpoint that links to it
     (header nav, drawer card, homepage hero, search shortcuts, footer) is
     authored ONCE, in Navigation or the Customizer, and cannot know who is
     looking.

     Liquid cannot personalise it either: it has no access to the
     fk_store_context cookie, and the rendered page is CDN-cached and served
     byte-identical to everyone. So the href is retargeted here, on the client,
     from the same context that already drives the pill and the {city} copy.

     Matched by DESTINATION, not by a marker attribute, because the main menu
     links come from Shopify Navigation, where no markup can be added. A link
     the operator adds later is therefore store-aware with no code change - it
     only has to point at one of the three Ready Today routes.

     Two exclusions:
       - [data-fk-store-confirm] - the controls that SET the context (the shelf
         picker, "Shop this store"). A control that CHOOSES a shop must never
         be retargeted by the shop already chosen, or Dallas becomes
         unreachable from Austin.
       - [data-fk-ready-link="fixed"] - explicit opt-out for a link that must
         always land where it was authored.
       - any link pointing at the page it is ON. On a shelf collection that is
         the pagination, the facet links, "clear all" and skip-to-content -
         in-page navigation, not a choice of destination. Retargeting those
         walks a shopper who confirmed Austin, then opened a shared Dallas
         link, straight out of the shelf they were reading the moment they
         pressed "load more".

     With no store chosen the href is restored to exactly what was authored.
     The operator owns the fallback; this only ever upgrades a known visitor. */

  function readyTargets() {
    var urls = [];
    var cfg = window.FKConfig && window.FKConfig.readyUrl;
    if (cfg) urls.push(String(cfg).replace(/\/+$/, ''));
    ((window.FKStores && window.FKStores.stores) || []).forEach(function (s) {
      if (s && s.readyUrl) urls.push(String(s.readyUrl).replace(/\/+$/, ''));
    });
    return urls;
  }

  function renderReadyLinks() {
    var C = ctx();
    if (!C) return;

    var targets = readyTargets();
    if (!targets.length) return;

    var key = C.get();
    var dest = key === 'none' ? null : readyUrl(key);
    var host = window.location.host;
    var here = window.location.pathname.replace(/\/+$/, '');

    document.querySelectorAll('a[href]').forEach(function (a) {
      if (a.hasAttribute('data-fk-store-confirm')) return;
      if (a.getAttribute('data-fk-ready-link') === 'fixed') return;
      /* Reading .host/.pathname off the anchor lets the DOM resolve the URL,
         so an absolute href pasted into Navigation is matched the same as a
         relative one - and mailto:/tel:/off-site links fall out here, because
         their host never equals ours. */
      if (a.host !== host) return;

      var path = a.pathname.replace(/\/+$/, '');
var chrome = !!a.closest('header, footer, [data-fk-nav-drawer], [data-fk-picker]');
if (path === here && !chrome) return;      /* in-page navigation */
      var stashed = a.hasAttribute('data-fk-ready-href');
      if (targets.indexOf(path) === -1 && !stashed) return;

      /* Stash what the operator authored, on first touch, so clearing the
         context ("shopping nationally") puts every link back rather than
         leaving it stuck on the last city visited. */
      if (!stashed) a.setAttribute('data-fk-ready-href', a.getAttribute('href'));

      var next = dest
        ? dest + a.search + a.hash
        : a.getAttribute('data-fk-ready-href');
      if (a.getAttribute('href') !== next) a.setAttribute('href', next);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Wiring                                                              */
  /* ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------------
     Live "when" phrases (snippets/fk-when.liquid). Same-day copy such as
     "we deliver it to Round Rock [today]" follows the shop's hours, today's
     delivery cutoff and the holiday closures, the same status the hero's
     order-by line reads, so the two can never disagree.
     ------------------------------------------------------------------------ */
  function whenText(key, kind) {
    var C = ctx();
    if (!C) return null;
    var s = C.status(key);
    if (!s || !s.known) return null;

    var next = (!s.nextOpenDayName || s.nextOpenDayName === 'tomorrow')
      ? 'tomorrow'
      : 'on ' + s.nextOpenDayName;
    var deliverToday = (s.state === 'open' || s.state === 'before-open') && !s.pastCutoff;
    var dayWord = deliverToday ? 'today' : next;
    if (kind === 'delivery') return dayWord;

    /* Pickup "within the hour" only while the shop is open with at least the
       ready time left before closing. */
    var now = C.now ? C.now() : null;
    var toClose = (s.closeHour != null && now) ? s.closeHour * 60 - now.minutes : 0;
    if (s.state === 'open' && toClose >= (s.readyMinutes || 60)) {
      return 'pick it up within the hour, or have it delivered ' + dayWord;
    }
    if (s.state === 'before-open' && deliverToday) {
      return 'pick it up or have it delivered today';
    }
    return 'pick it up or have it delivered ' + next;
  }

  function renderWhen() {
    var C = ctx();
    if (!C) return;
    document.querySelectorAll('[data-fk-when]').forEach(function (el) {
      var key = el.getAttribute('data-fk-when');
      if (key === 'auto') {
        key = C.get();
        if (!key || key === 'none') key = el.getAttribute('data-fk-when-default') || 'austin';
      }
      var t = whenText(key, el.getAttribute('data-fk-when-kind'));
      if (!t) return;
      if (el.hasAttribute('data-fk-when-cap')) t = t.charAt(0).toUpperCase() + t.slice(1);
      if (el.textContent !== t) el.textContent = t;
    });
  }

  function renderAll() {
    renderWhen();
    renderPills();
    renderCityTokens();
    renderReadyLinks();
    renderStoreCards();
    renderStorePage();
    var picker = document.querySelector('[data-fk-picker]');
    if (picker && !picker.hidden) renderPicker();
  }

  /* Landing on a store page is an explicit signal — suggest that store, but
     leave it unconfirmed so the shopper still sees the confirm affordance. */
  function suggestFromPage() {
    var C = ctx();
    if (!C) return;
    if (C.get() !== 'none') return;

    var page = document.querySelector('[data-fk-store-page]');
    if (page) {
      C.suggest(page.getAttribute('data-fk-store-page'));
      return;
    }

    /* A shelf COLLECTION is the same kind of signal as a store page: every
       product on it is physically in one shop. Someone arriving on
       /collections/ready-dallas from search or a shared link is telling us as
       much as someone landing on /pages/dallas — and without this they would
       be reading the Dallas shelf under a header that still says "Choose your
       store" and a nav that still points at Austin.

       fk-ready-today stamps the shop it is pinned to on its own root, so read
       that rather than re-parsing the collection handle here. Still a suggest,
       never a confirm: the pill renders "Dallas?" and the shopper keeps the
       affordance to say otherwise. */
    var shelf = document.querySelector('[data-fk-ready][data-fk-pinned]');
    if (shelf) C.suggest(shelf.getAttribute('data-fk-pinned'));
  }

  document.addEventListener('click', function (e) {
    var C = ctx();

    if (e.target.closest('[data-fk-store-pill]') || e.target.closest('[data-fk-open-picker]')) {
      e.preventDefault();
      openPicker();
      return;
    }

    if (e.target.closest('[data-fk-picker-close]')) {
      e.preventDefault();
      closePicker();
      return;
    }

    /* SET THE STORE, THEN LET THE LINK GO. Deliberately no preventDefault —
       this is the opposite of data-fk-pick below, and the whole reason it is a
       separate hook.

       Only acts when the attribute carries a store key. The store detail page
       renders a BARE `data-fk-store-confirm` and reads the key from the page
       itself in its own inline handler; matching that here would call
       C.set("") and wipe the context on the way to Ready Today. */
    var confirm = e.target.closest('[data-fk-store-confirm]');
    if (confirm && C) {
      var confirmKey = confirm.getAttribute('data-fk-store-confirm');
      if (confirmKey) C.set(confirmKey, true);
      return;
    }

    var pick = e.target.closest('[data-fk-pick]');
    if (pick && C) {
      e.preventDefault();
      C.set(pick.getAttribute('data-fk-pick'), true);
      closePicker();
      return;
    }

    if (e.target.closest('[data-fk-pick-national]') && C) {
      e.preventDefault();
      C.set('none', true);
      closePicker();
      return;
    }

    var zipBtn = e.target.closest('[data-fk-zip-submit]');
    if (zipBtn) {
      e.preventDefault();
      var scope = zipBtn.closest('[data-fk-picker], [data-fk-zip-scope]') || document;
      runZipCheck(scope);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closePicker();
    if (e.key === 'Enter' && e.target && e.target.hasAttribute && e.target.hasAttribute('data-fk-zip-input')) {
      e.preventDefault();
      var scope = e.target.closest('[data-fk-picker], [data-fk-zip-scope]') || document;
      runZipCheck(scope);
    }
  });

  document.addEventListener('fk:store-context-changed', renderAll);
  document.addEventListener('fk:tick', renderAll);
  document.addEventListener('shopify:section:load', renderAll);

  function boot() {
    suggestFromPage();
    renderAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
